import type { SupportedLocale } from "utility/locale";

import enGBCommon from "../../../../packages/locales/en-GB/common.json";
import enGBAuth from "../../../../packages/locales/en-GB/auth.json";
import enGBOnboard from "../../../../packages/locales/en-GB/onboard.json";
import enGBIndex from "../../../../packages/locales/en-GB/index.json";
import enGBSettings from "../../../../packages/locales/en-GB/settings.json";
import enGBBiometrics from "../../../../packages/locales/en-GB/biometrics.json";
import enGBOri from "../../../../packages/locales/en-GB/ori.json";
import enGBAssessment from "../../../../packages/locales/en-GB/assessment.json";
import enGBComponents from "../../../../packages/locales/en-GB/components.json";
import enGBDiscover from "../../../../packages/locales/en-GB/discover.json";
import enGBProfile from "../../../../packages/locales/en-GB/profile.json";
import enGBHome from "../../../../packages/locales/en-GB/home.json";
import enGBSessions from "../../../../packages/locales/en-GB/sessions.json";
import enGBFeedback from "../../../../packages/locales/en-GB/feedback.json";
import enGBAdmin from "../../../../packages/locales/en-GB/admin.json";
import enGBDecemberDoors from "../../../../packages/locales/en-GB/decemberDoors.json";
import frFRCommon from "../../../../packages/locales/fr-FR/common.json";
import frFRAuth from "../../../../packages/locales/fr-FR/auth.json";
import frFROnboard from "../../../../packages/locales/fr-FR/onboard.json";
import frFRIndex from "../../../../packages/locales/fr-FR/index.json";
import frFRSettings from "../../../../packages/locales/fr-FR/settings.json";
import frFRBiometrics from "../../../../packages/locales/fr-FR/biometrics.json";
import frFROri from "../../../../packages/locales/fr-FR/ori.json";
import frFRAssessment from "../../../../packages/locales/fr-FR/assessment.json";
import frFRComponents from "../../../../packages/locales/fr-FR/components.json";
import frFRDiscover from "../../../../packages/locales/fr-FR/discover.json";
import frFRProfile from "../../../../packages/locales/fr-FR/profile.json";
import frFRHome from "../../../../packages/locales/fr-FR/home.json";
import frFRSessions from "../../../../packages/locales/fr-FR/sessions.json";
import frFRFeedback from "../../../../packages/locales/fr-FR/feedback.json";
import frFRAdmin from "../../../../packages/locales/fr-FR/admin.json";
import frFRDecemberDoors from "../../../../packages/locales/fr-FR/decemberDoors.json";
import ptPTCommon from "../../../../packages/locales/pt-PT/common.json";
import ptPTAuth from "../../../../packages/locales/pt-PT/auth.json";
import ptPTOnboard from "../../../../packages/locales/pt-PT/onboard.json";
import ptPTIndex from "../../../../packages/locales/pt-PT/index.json";
import ptPTSettings from "../../../../packages/locales/pt-PT/settings.json";
import ptPTBiometrics from "../../../../packages/locales/pt-PT/biometrics.json";
import ptPTOri from "../../../../packages/locales/pt-PT/ori.json";
import ptPTAssessment from "../../../../packages/locales/pt-PT/assessment.json";
import ptPTComponents from "../../../../packages/locales/pt-PT/components.json";
import ptPTDiscover from "../../../../packages/locales/pt-PT/discover.json";
import ptPTProfile from "../../../../packages/locales/pt-PT/profile.json";
import ptPTHome from "../../../../packages/locales/pt-PT/home.json";
import ptPTSessions from "../../../../packages/locales/pt-PT/sessions.json";
import ptPTFeedback from "../../../../packages/locales/pt-PT/feedback.json";
import ptPTAdmin from "../../../../packages/locales/pt-PT/admin.json";
import ptPTDecemberDoors from "../../../../packages/locales/pt-PT/decemberDoors.json";
import esESCommon from "../../../../packages/locales/es-ES/common.json";
import esESAuth from "../../../../packages/locales/es-ES/auth.json";
import esESOnboard from "../../../../packages/locales/es-ES/onboard.json";
import esESIndex from "../../../../packages/locales/es-ES/index.json";
import esESSettings from "../../../../packages/locales/es-ES/settings.json";
import esESBiometrics from "../../../../packages/locales/es-ES/biometrics.json";
import esESOri from "../../../../packages/locales/es-ES/ori.json";
import esESAssessment from "../../../../packages/locales/es-ES/assessment.json";
import esESComponents from "../../../../packages/locales/es-ES/components.json";
import esESDiscover from "../../../../packages/locales/es-ES/discover.json";
import esESProfile from "../../../../packages/locales/es-ES/profile.json";
import esESHome from "../../../../packages/locales/es-ES/home.json";
import esESSessions from "../../../../packages/locales/es-ES/sessions.json";
import esESFeedback from "../../../../packages/locales/es-ES/feedback.json";
import esESAdmin from "../../../../packages/locales/es-ES/admin.json";
import esESDecemberDoors from "../../../../packages/locales/es-ES/decemberDoors.json";

import enGBAuthOverrides from "./overrides/en-GB/auth.json";
import frFRAuthOverrides from "./overrides/fr-FR/auth.json";

/**
 * Namespaces split the catalog by feature area so the web sweep can grow one
 * file per screen group rather than one blob. `common` holds cross-cutting
 * copy; `auth`, `onboard`, `index`, `settings`, `ori`, `assessment`, `home`,
 * `discover`, `profile`, `sessions`, `feedback`, `components` and `admin` (the
 * corporate-admin portal at `/client/*`) are sweep slices; `decemberDoors`
 * is the holiday-season door calendar, one catalog shared with mobile so the
 * copy is written and translated once; `biometrics` is
 * registered purely for exact-match reuse of the wearable-connect copy owned
 * by `settings`. The catalogs themselves are the monorepo-shared ones in
 * `frontend/packages/locales/` (see its README) — web registers only the namespaces it
 * renders, so it never bundles mobile-only copy.
 */
export const NAMESPACES = [
  "common",
  "auth",
  "onboard",
  "index",
  "settings",
  "biometrics",
  "ori",
  "assessment",
  "components",
  "discover",
  "profile",
  "home",
  "sessions",
  "feedback",
  "admin",
  "decemberDoors",
] as const;
export type Namespace = typeof NAMESPACES[number];

export const DEFAULT_NAMESPACE: Namespace = "common";

type Catalog = { [key: string]: string | Catalog };

/**
 * Web-specific copy that intentionally diverges from the shared catalogs
 * (e.g. `auth:otp.titleSignIn` — "Welcome back" on web vs "Sign in" on
 * mobile). Override values win over shared ones; a locale with no override
 * file falls back to the shared (translated) value.
 */
const overrides: Partial<
  Record<SupportedLocale, Partial<Record<Namespace, Catalog>>>
> = {
  "en-GB": { auth: enGBAuthOverrides as Catalog },
  "fr-FR": { auth: frFRAuthOverrides as Catalog },
};

/**
 * Throws (at module init, i.e. first test/build) rather than merging
 * silently: an override key with no shared counterpart is a typo or a
 * drifted key that would otherwise quietly stop overriding, and a
 * shape mismatch would quietly clobber a whole subtree.
 */
const mergeCatalog = (
  path: string,
  shared: Catalog,
  override?: Catalog
): Catalog => {
  if (!override) return shared;
  const merged: Catalog = { ...shared };
  for (const [key, value] of Object.entries(override)) {
    const base = merged[key];
    if (base === undefined) {
      throw new Error(
        `i18n override key "${path}.${key}" has no counterpart in the shared catalog — overrides may only diverge from existing shared keys`
      );
    }
    if ((typeof value === "object") !== (typeof base === "object")) {
      throw new Error(
        `i18n override shape mismatch at "${path}.${key}" — override and shared must both be strings or both be objects`
      );
    }
    merged[key] =
      typeof value === "object" && typeof base === "object"
        ? mergeCatalog(`${path}.${key}`, base as Catalog, value)
        : value;
  }
  return merged;
};

const compose = (
  locale: SupportedLocale,
  catalogs: Record<Namespace, Catalog>
): Record<Namespace, object> => {
  const result = {} as Record<Namespace, Catalog>;
  for (const ns of NAMESPACES) {
    result[ns] = mergeCatalog(
      `${locale}:${ns}`,
      catalogs[ns],
      overrides[locale]?.[ns]
    );
  }
  return result;
};

/**
 * Bundled catalogs — all four supported locales, straight from the shared
 * `frontend/packages/locales/` source of truth (fr/pt/es machine-translated, review
 * gated by feature flag). Keys with an empty value in a locale fall back to
 * `en-GB` (see `fallbackLng` + `returnEmptyString: false` in ./index).
 */
export const resources: Partial<
  Record<SupportedLocale, Record<Namespace, object>>
> = {
  "en-GB": compose("en-GB", {
    common: enGBCommon as Catalog,
    auth: enGBAuth as Catalog,
    onboard: enGBOnboard as Catalog,
    index: enGBIndex as Catalog,
    settings: enGBSettings as Catalog,
    biometrics: enGBBiometrics as Catalog,
    ori: enGBOri as Catalog,
    assessment: enGBAssessment as Catalog,
    components: enGBComponents as Catalog,
    discover: enGBDiscover as Catalog,
    profile: enGBProfile as Catalog,
    home: enGBHome as Catalog,
    sessions: enGBSessions as Catalog,
    feedback: enGBFeedback as Catalog,
    admin: enGBAdmin as Catalog,
    decemberDoors: enGBDecemberDoors as Catalog,
  }),
  "fr-FR": compose("fr-FR", {
    common: frFRCommon as Catalog,
    auth: frFRAuth as Catalog,
    onboard: frFROnboard as Catalog,
    index: frFRIndex as Catalog,
    settings: frFRSettings as Catalog,
    biometrics: frFRBiometrics as Catalog,
    ori: frFROri as Catalog,
    assessment: frFRAssessment as Catalog,
    components: frFRComponents as Catalog,
    discover: frFRDiscover as Catalog,
    profile: frFRProfile as Catalog,
    home: frFRHome as Catalog,
    sessions: frFRSessions as Catalog,
    feedback: frFRFeedback as Catalog,
    admin: frFRAdmin as Catalog,
    decemberDoors: frFRDecemberDoors as Catalog,
  }),
  "pt-PT": compose("pt-PT", {
    common: ptPTCommon as Catalog,
    auth: ptPTAuth as Catalog,
    onboard: ptPTOnboard as Catalog,
    index: ptPTIndex as Catalog,
    settings: ptPTSettings as Catalog,
    biometrics: ptPTBiometrics as Catalog,
    ori: ptPTOri as Catalog,
    assessment: ptPTAssessment as Catalog,
    components: ptPTComponents as Catalog,
    discover: ptPTDiscover as Catalog,
    profile: ptPTProfile as Catalog,
    home: ptPTHome as Catalog,
    sessions: ptPTSessions as Catalog,
    feedback: ptPTFeedback as Catalog,
    admin: ptPTAdmin as Catalog,
    decemberDoors: ptPTDecemberDoors as Catalog,
  }),
  "es-ES": compose("es-ES", {
    common: esESCommon as Catalog,
    auth: esESAuth as Catalog,
    onboard: esESOnboard as Catalog,
    index: esESIndex as Catalog,
    settings: esESSettings as Catalog,
    biometrics: esESBiometrics as Catalog,
    ori: esESOri as Catalog,
    assessment: esESAssessment as Catalog,
    components: esESComponents as Catalog,
    discover: esESDiscover as Catalog,
    profile: esESProfile as Catalog,
    home: esESHome as Catalog,
    sessions: esESSessions as Catalog,
    feedback: esESFeedback as Catalog,
    admin: esESAdmin as Catalog,
    decemberDoors: esESDecemberDoors as Catalog,
  }),
};
